/** /pre_trip_item_types/ — cross-tenant pre-trip item CATEGORY catalog.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). Categories have no `updated_ts` — writes are
 * last-writer-wins (no optimistic-concurrency token, unlike tours / trip types).
 * `is_system` rows (null-tenant shared defaults) are read-only: a PATCH/DELETE
 * returns 403, so the UI hides those affordances.
 */
import { apiDelete, apiGet, apiPatch, apiPost } from './client';
import type { TenantRef } from './tours';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

/** 1 Text · 2 List with checkboxes · 3 Link · 4 List. */
export type RenderingType = 1 | 2 | 3 | 4;

export const RENDERING_TYPE_LABELS: Record<RenderingType, string> = {
  1: 'Text',
  2: 'List with checkboxes',
  3: 'Link',
  4: 'List',
};

export interface PreTripItemTypeRow {
  id: number;
  /** Owning tenant; null for shared system-default categories (`is_system`). */
  tenant: TenantRef | null;
  /** Human display name (model `display_name`). */
  name: string;
  /** Internal name (model `name`). */
  system_name: string;
  rendering_type: RenderingType;
  rendering_type_display: string | null;
  /** Items in this category (CASCADE-deleted with it). */
  item_count: number;
  /** True for shared system defaults (null tenant) — read-only in the console. */
  is_system: boolean;
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
  rendering_type?: RenderingType;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.tour_operator_id !== undefined) usp.set('tour_operator_id', String(params.tour_operator_id));
  if (params.rendering_type !== undefined) usp.set('rendering_type', String(params.rendering_type));
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<PreTripItemTypeRow>> {
  return apiGet<PaginatedResponse<PreTripItemTypeRow>>(`/pre_trip_item_types/${toQuery(params)}`);
}

export function retrieve(id: number): Promise<PreTripItemTypeRow> {
  return apiGet<PreTripItemTypeRow>(`/pre_trip_item_types/${id}/`);
}

/** Create body — all three fields required; `name` = display name,
 *  `system_name` = internal name. */
export interface PreTripItemTypeCreateInput {
  tour_operator: number;
  name: string;
  system_name: string;
  rendering_type: RenderingType;
}

export function create(body: PreTripItemTypeCreateInput): Promise<PreTripItemTypeRow> {
  return apiPost<PreTripItemTypeRow>('/pre_trip_item_types/', body);
}

/** PATCH body — any subset. `is_system` rows return 403. */
export interface PreTripItemTypeWriteInput {
  name?: string;
  system_name?: string;
  rendering_type?: RenderingType;
}

export function update(id: number, body: PreTripItemTypeWriteInput): Promise<PreTripItemTypeRow> {
  return apiPatch<PreTripItemTypeRow>(`/pre_trip_item_types/${id}/`, body);
}

export function remove(id: number): Promise<void> {
  return apiDelete(`/pre_trip_item_types/${id}/`);
}
